export const familyLetters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"] as const;

export type FamilyLetter = (typeof familyLetters)[number];

const familyUnitLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function getFamilyUnitSequence(value: unknown): number | null {
  const normalized = String(value ?? "").trim().toUpperCase();

  if (!normalized) {
    return null;
  }

  const numericMatch = /^0*(\d{1,3})$/.exec(normalized);
  const familyNumberMatch =
    /^(?:FAM|FAMILY|FAM\s*UNIT|FAMILY\s*UNIT|UNIT)[\s_#-]*0*(\d{1,3})$/.exec(normalized);
  const sequence = Number(numericMatch?.[1] ?? familyNumberMatch?.[1]);

  return Number.isInteger(sequence) && sequence > 0 ? sequence : null;
}

function getFamilyCodeLetter(value: unknown): string | null {
  const normalized = String(value ?? "").trim().toUpperCase();
  const letterMatch = /^(?:FAMILY|FAM)?\s*([A-Z])$/.exec(normalized);

  return letterMatch ? letterMatch[1] : null;
}

function getFamilyLetter(value: unknown): FamilyLetter | null {
  const letter = getFamilyCodeLetter(value);

  return familyLetters.includes(letter as FamilyLetter) ? letter as FamilyLetter : null;
}

export function familyCodeToUnitNo(value: unknown, fallback = "FAM-001"): string {
  const letter = getFamilyCodeLetter(value);

  if (!letter) {
    return fallback;
  }

  const sequence = familyUnitLetters.indexOf(letter) + 1;

  return sequence > 0 ? `FAM-${String(sequence).padStart(3, "0")}` : fallback;
}

export function familyLetterToUnitNo(value: unknown): string {
  const letter = normalizeFamilyLetter(value);
  const sequence = familyLetters.indexOf(letter) + 1;
  return `FAM-${String(sequence).padStart(3, "0")}`;
}

export function familyUnitNoToLetter(value: unknown): FamilyLetter {
  const sequence = getFamilyUnitSequence(value) ?? 0;
  return familyLetters[sequence - 1] ?? "A";
}

export function normalizeFamilyLetter(value: unknown): FamilyLetter {
  const normalized = String(value ?? "").trim().toUpperCase();

  if (familyLetters.includes(normalized as FamilyLetter)) {
    return normalized as FamilyLetter;
  }

  const sequence = getFamilyUnitSequence(normalized);

  if (sequence !== null) {
    return familyLetters[sequence - 1] ?? "A";
  }

  return getFamilyLetter(normalized) ?? "A";
}

export function normalizeFamilyUnitNo(value: unknown, fallback = "FAM-001"): string {
  const normalized = String(value ?? "").trim().toUpperCase();

  if (!normalized) {
    return fallback;
  }

  const sequence = getFamilyUnitSequence(normalized);

  if (sequence !== null) {
    return `FAM-${String(sequence).padStart(3, "0")}`;
  }

  const letter = getFamilyCodeLetter(normalized);

  if (letter) {
    return familyCodeToUnitNo(letter, fallback);
  }

  return normalized.replace(/\s+/g, "-");
}

export function getFamilyDisplayName(unitNo: unknown, familyName?: unknown): string {
  const normalizedName = String(familyName ?? "").trim();
  const normalizedNameUpper = normalizedName.toUpperCase();

  if (normalizedNameUpper === "PRIMARY FAMILY") {
    return "Family A";
  }

  if (/^FAMILY\s+[A-Z]$/i.test(normalizedName)) {
    return `Family ${getFamilyCodeLetter(normalizedName) ?? normalizeFamilyLetter(normalizedName)}`;
  }

  const sequence = getFamilyUnitSequence(unitNo);

  if (sequence !== null && sequence > 0 && sequence <= familyUnitLetters.length) {
    return `Family ${familyUnitLetters[sequence - 1]}`;
  }

  if (sequence !== null && sequence > familyLetters.length) {
    return `Family ${sequence}`;
  }

  return `Family ${familyUnitNoToLetter(unitNo)}`;
}
